import { observeManyWhenVisible } from '../../../core/utils/dom.js';
import { CDN_URL } from '../../config.js';
import { applyTranslationsToContainer, getCurrentLanguage } from '../../translations.js';

const toStr = (v) => (v === null || v === undefined ? '' : String(v));
const normalize = (v) => toStr(v).toLowerCase().trim();

const getSelectedTimeOfDayValue = (timeSelectId = 'time-of-day-1') => {
  const raw = toStr(document.getElementById(timeSelectId)?.value || '0.1').trim();
  return raw || '0.1';
};

const getTimeSuffix = (timeOfDayValue) => {
  const suffix = toStr(timeOfDayValue).split('.')[1] || '1';
  return (suffix === '1' || suffix === '2' || suffix === '3') ? suffix : '1';
};

const getCdnBackgroundUrlForOption = (optionValue, timeOfDayValue) => {
  const baseId = Math.floor(Number(optionValue));
  if (!Number.isFinite(baseId) || baseId <= 0) return null;
  const suffix = getTimeSuffix(timeOfDayValue);
  return `${CDN_URL}/data/backgrounds_images/${baseId}.${suffix}.webp`;
};

const getSyntheticBackgroundUrlForOption = (syntheticId, timeOfDayValue) => {
  const map = window.syntheticBackgroundPreviews && typeof window.syntheticBackgroundPreviews === 'object'
    ? window.syntheticBackgroundPreviews
    : null;
  if (!map) return null;

  const exactKey = `${syntheticId}.${timeOfDayValue}`;
  const exact = toStr(map[exactKey]).trim();
  if (exact) return exact;

  // Back-compat: some flows cached without TOD suffix.
  const raw = toStr(map[syntheticId]).trim();
  if (raw) return raw;

  const anyKey = Object.keys(map).find((k) => toStr(k).startsWith(`${syntheticId}.`));
  const anyUrl = anyKey ? toStr(map[anyKey]).trim() : '';
  return anyUrl || null;
};

const ensureModal = () => {
  let modal = document.getElementById('create-location-picker-modal');
  if (modal) return modal;

  modal = document.createElement('div');
  modal.id = 'create-location-picker-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-label', 'Select a location');

  const panel = document.createElement('div');
  // Reuse the same visual language as the dropdown panels in create.css
  panel.className = 'picker-panel search-dropdown__panel';

  modal.appendChild(panel);
  document.body.appendChild(modal);
  return modal;
};

const openModal = (modal) => {
  if (!modal) return;
  modal.classList.add('show');
};

const closeModal = (modal) => {
  if (!modal) return;
  modal.classList.remove('show');
};

const getLocationOptionsFromSelect = (selectEl) => {
  const opts = Array.from(selectEl?.options || []);
  return opts
    .map((o) => ({
      value: toStr(o.value).trim(),
      label: toStr(o.textContent).trim(),
      disabled: !!o.disabled,
      isCustom: toStr(o.value).startsWith('new-bg-')
    }))
    .filter((o) => !!o.value)
    .filter((o) => !!o.label);
};

const sortLocationOptions = (options, currentValue) => {
  const cur = toStr(currentValue).trim();
  const list = Array.isArray(options) ? [...options] : [];
  list.sort((a, b) => {
    // Match character picker behavior: custom entries first.
    if (!!a.isCustom !== !!b.isCustom) return a.isCustom ? -1 : 1;

    // Within each group, pin the current selection to the top.
    const aSel = a?.value === cur;
    const bSel = b?.value === cur;
    if (aSel !== bSel) return aSel ? -1 : 1;

    // Stable-ish fallback: alphabetical by label.
    return toStr(a?.label).localeCompare(toStr(b?.label));
  });
  return list;
};

export const openCreateLocationPicker = ({
  selectId,
  timeSelectId = 'time-of-day-1',
  options,
  onSelect,
  allowCreate = true,
} = {}) => {
  const selectEl = selectId ? document.getElementById(selectId) : null;
  if (!selectEl && typeof onSelect !== 'function') return;

  const modal = ensureModal();
  const panel = modal.querySelector('.picker-panel');
  if (!panel) return;

  let searchTerm = '';
  let isClosing = false;
  let bgObserver = null;

  const currentValue = selectEl ? toStr(selectEl.value).trim() : '';
  const allOptions = Array.isArray(options) ? options : getLocationOptionsFromSelect(selectEl);
  const timeOfDayValue = getSelectedTimeOfDayValue(timeSelectId);

  const disconnectObserver = () => {
    bgObserver?.disconnect();
    bgObserver = null;
  };

  const handleClose = () => {
    if (isClosing) return;
    isClosing = true;
    disconnectObserver();
    closeModal(modal);
    document.removeEventListener('keydown', onKeyDownCapture, true);
  };

  const onKeyDownCapture = (event) => {
    const key = event.key || event.code;
    if (key === 'Escape' || key === 'Esc') {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      handleClose();
    }
  };

  const render = () => {
    disconnectObserver();
    panel.innerHTML = '';

    const header = document.createElement('div');
    header.className = 'picker-header';

    const title = document.createElement('div');
    title.className = 'picker-title';
    title.textContent = 'Select a location';

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'picker-close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.tabIndex = 0;
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', handleClose);

    header.appendChild(title);
    header.appendChild(closeBtn);

    const searchWrap = document.createElement('div');
    searchWrap.className = 'picker-search';

    const searchInput = document.createElement('input');
    searchInput.type = 'text';
    searchInput.value = searchTerm;
    searchInput.placeholder = 'Search locations...';
    searchInput.setAttribute('aria-label', 'Search locations');
    searchInput.addEventListener('input', (e) => {
      searchTerm = e?.target?.value || '';
      render();
    });

    searchWrap.appendChild(searchInput);

    const listWrap = document.createElement('div');
    listWrap.className = 'picker-list';

    const grid = document.createElement('div');
    grid.className = 'picker-grid';

    const q = normalize(searchTerm);
    const base = !q
      ? allOptions
      : allOptions.filter((o) => normalize(o.label).includes(q));
    const filtered = sortLocationOptions(base, currentValue);

    if (filtered.length) {
      const applyBg = (el) => {
        const url = toStr(el?.dataset?.bgUrl || '').trim();
        if (url) el.style.backgroundImage = `url(${url})`;
      };
      bgObserver = observeManyWhenVisible([], applyBg,
        { once: true, root: listWrap, rootMargin: '220px 0px', fallback: applyBg });

      const frag = document.createDocumentFragment();
      filtered.forEach((opt) => {
        const isSelected = opt.value === currentValue;

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `location-card${isSelected ? ' location-card--selected' : ''}`;
        btn.disabled = !!opt.disabled;
        btn.tabIndex = 0;
        btn.setAttribute('aria-label', `Select ${opt.label}`);

        const bgUrl = opt.isCustom
          ? getSyntheticBackgroundUrlForOption(opt.value, timeOfDayValue)
          : getCdnBackgroundUrlForOption(opt.value, timeOfDayValue);

        if (bgUrl) {
          btn.dataset.bgUrl = bgUrl;
          bgObserver.observe(btn);
        }

        const overlay = document.createElement('div');
        overlay.className = 'location-overlay';

        const name = document.createElement('div');
        name.className = 'location-name';
        name.textContent = opt.label;
        overlay.appendChild(name);

        if (opt.isCustom) {
          const subtitle = document.createElement('div');
          subtitle.className = 'location-subtitle';
          subtitle.textContent = 'Custom';
          overlay.appendChild(subtitle);
        }

        btn.appendChild(overlay);

        btn.addEventListener('click', () => {
          if (btn.disabled) return;
          if (typeof onSelect === 'function') {
            onSelect(opt);
            handleClose();
            return;
          }
          if (selectEl && selectEl.value !== opt.value) {
            selectEl.value = opt.value;
            selectEl.dispatchEvent(new Event('change', { bubbles: true }));
          }
          handleClose();
        });

        frag.appendChild(btn);
      });
      grid.appendChild(frag);
    }

    if (allowCreate) {
      const createCard = document.createElement('button');
      createCard.type = 'button';
      createCard.className = 'location-card create-background-card';
      createCard.tabIndex = 0;
      createCard.setAttribute('aria-label', 'Create a background');
      createCard.innerHTML = `
        <div class="create-background-inner">
          <div class="create-background-plus" aria-hidden="true">+</div>
          <div class="create-background-title" data-translate="steps.setting.create_background">Create a background</div>
        </div>`;
      const handleCreateBackground = () => {
        if (selectEl) {
          selectEl.selectedIndex = 0;
          selectEl.dispatchEvent(new Event('change', { bubbles: true }));
        }

        const previewDiv = document.getElementById('bg-preview-1');
        if (previewDiv) {
          previewDiv.innerHTML = '';
          previewDiv.style.backgroundImage = '';
          const placeholder = document.createElement('span');
          placeholder.style.cssText = 'color:rgba(255,255,255,0.5);font-style:italic;';
          placeholder.setAttribute('data-translate', 'steps.setting.stage_preview_placeholder');
          previewDiv.appendChild(placeholder);
          applyTranslationsToContainer(previewDiv, 'create-scenario', getCurrentLanguage());
        }

        handleClose();
        if (typeof window.showCustomBackgroundUI === 'function') window.showCustomBackgroundUI();
      };

      createCard.addEventListener('click', handleCreateBackground);
      createCard.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        handleCreateBackground();
      });
      grid.appendChild(createCard);
    }

    listWrap.appendChild(grid);

    panel.appendChild(header);
    panel.appendChild(searchWrap);
    panel.appendChild(listWrap);

    requestAnimationFrame(() => {
      searchInput.focus();
    });
  };

  // Click outside closes
  modal.onclick = (e) => {
    if (e.target === modal) handleClose();
  };

  document.addEventListener('keydown', onKeyDownCapture, true);
  render();
  openModal(modal);
};

export const attachCreateLocationPickerToTrigger = ({ selectId, timeSelectId = 'time-of-day-1' } = {}) => {
  if (!selectId) return;
  const trigger = document.getElementById(`${selectId}-search-trigger`);
  if (!trigger) return;

  const handleOpen = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    }
    // Guard against duplicate opens from touchstart + click.
    if (trigger.dataset.pickerOpening === 'true') return;
    trigger.dataset.pickerOpening = 'true';
    try {
      openCreateLocationPicker({ selectId, timeSelectId });
    } finally {
      setTimeout(() => { trigger.dataset.pickerOpening = 'false'; }, 0);
    }
  };

  // Capture phase prevents the searchDropdown's own click/keydown handlers from firing.
  trigger.addEventListener('click', handleOpen, true);
  trigger.addEventListener('touchstart', handleOpen, { capture: true, passive: false });
  trigger.addEventListener('pointerdown', handleOpen, { capture: true });
  trigger.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') return handleOpen(e);
  }, true);
};

